/**
 * A single Pointer Events code path for mouse, touch and pen.
 *
 * No device branching: pointerdown/pointermove/pointerup drive the same
 * logic for a mouse on a laptop, a finger on an iPad, and a touchscreen
 * laptop. `event.pointerType` is read only for cosmetics (the touch
 * fingertip offset applied by the caller's `hitTest`).
 *
 * This class knows nothing about the puzzle — it only turns pointer
 * coordinates into tile ids via the supplied `hitTest`, and forwards
 * down/move/up as tile-id events. All path logic lives in the caller.
 */

/**
 * Whether a point is the start of a gesture or part of one already running.
 *
 * The caller needs this because the two want opposite things: a fresh tap
 * should be read exactly where the finger is and be generous about near
 * misses, while a drag in progress wants the fingertip offset and strict
 * tile-to-tile accuracy. Passing the phase keeps that decision in `hitTest`,
 * where the board geometry lives, rather than duplicating gesture state here.
 */
export type PointerPhase = "down" | "move";

export interface PathInputHandlers {
  /** Resolve a client-space point to a tile id, or null if it misses the board. */
  hitTest(clientX: number, clientY: number, pointerType: string, phase: PointerPhase): number | null;
  onDown(id: number | null): void;
  onMove(id: number | null): void;
  onUp(): void;
}

export class PathInput {
  private activePointerId: number | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly handlers: PathInputHandlers,
  ) {
    // Belt-and-suspenders with the CSS touch-action: none on the canvas —
    // this is what stops a drag from scrolling the page on mobile.
    this.canvas.style.touchAction = "none";

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerUp);
  }

  destroy(): void {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerUp);
  }

  private onPointerDown(e: PointerEvent): void {
    // Ignore a second finger while one drag is already active.
    if (this.activePointerId !== null) return;
    this.activePointerId = e.pointerId;
    // Capture so a fast drag past the canvas edge keeps delivering events here.
    this.canvas.setPointerCapture(e.pointerId);
    const id = this.handlers.hitTest(e.clientX, e.clientY, e.pointerType, "down");
    this.handlers.onDown(id);
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerId !== this.activePointerId) return;
    const id = this.handlers.hitTest(e.clientX, e.clientY, e.pointerType, "move");
    this.handlers.onMove(id);
  }

  private onPointerUp(e: PointerEvent): void {
    if (e.pointerId !== this.activePointerId) return;
    this.activePointerId = null;
    this.handlers.onUp();
  }
}
